/**
 * 统一错误码 → HTTP 状态码 → 中文提示
 *
 * 前端只依赖 code 做分支，message 永远是可以直接展示给用户的中文文案。
 * 新增错误码时必须同时补齐状态码与中文提示，否则 typecheck 会失败。
 */

export const ERROR_CODES = [
  // 4xx — 客户端问题
  'VALIDATION_FAILED',
  'AUTH_REQUIRED',
  'INVALID_CREDENTIALS',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'FORBIDDEN',
  'NOT_FOUND',
  'EMAIL_EXISTS',
  'PAYLOAD_TOO_LARGE',
  'COPYRIGHT_ACK_REQUIRED',
  'RATE_LIMITED',

  // 5xx — 服务端 / 上游问题
  'UPSTREAM_DICTIONARY_FAILED',
  'AI_UNAVAILABLE',
  'DB_UNAVAILABLE',
  'INTERNAL',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ERROR_STATUS: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  AUTH_REQUIRED: 401,
  INVALID_CREDENTIALS: 401,
  TOKEN_EXPIRED: 401,
  TOKEN_INVALID: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  EMAIL_EXISTS: 409,
  PAYLOAD_TOO_LARGE: 413,
  COPYRIGHT_ACK_REQUIRED: 422,
  RATE_LIMITED: 429,
  UPSTREAM_DICTIONARY_FAILED: 502,
  AI_UNAVAILABLE: 503,
  DB_UNAVAILABLE: 503,
  INTERNAL: 500,
};

export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: '输入内容不合法，请检查后重试',
  AUTH_REQUIRED: '请先登录后再使用该功能',
  INVALID_CREDENTIALS: '邮箱或密码不正确',
  TOKEN_EXPIRED: '登录已过期，请重新登录',
  TOKEN_INVALID: '登录凭证无效，请重新登录',
  FORBIDDEN: '你没有权限执行该操作',
  NOT_FOUND: '未找到相关内容',
  EMAIL_EXISTS: '该邮箱已注册，请直接登录',
  PAYLOAD_TOO_LARGE: '内容过长，请拆分后分批导入',
  COPYRIGHT_ACK_REQUIRED: '请先确认你拥有该文本的使用授权',
  RATE_LIMITED: '请求过于频繁，请稍后再试',
  UPSTREAM_DICTIONARY_FAILED: '词典服务暂时不可用，已展示缓存结果',
  AI_UNAVAILABLE: 'AI 分析服务暂时不可用，请稍后重试',
  DB_UNAVAILABLE: '数据服务暂时不可用，请稍后重试',
  INTERNAL: '服务器开小差了，请稍后重试',
};

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}
